
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Columns3 } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import { getJwtRoleSlug } from "../../../public/utils/jwtRole";
import {
  createDonor,
  getAllDonors,
  getDonorDashboard,
  getDonorDetails,
  searchDonors,
  updateDonor,
  updateDonorStatus,
} from "../api/donorsApi";
import { getCsrfToken } from "../../../public/api/authApi";
import GlobalQuickJump from "../../../global/GlobalQuickJump";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import { GlobalModal } from "../../../global/GlobalModal";
import GlobalMetricCards from "../../../global/GlobalMetricCards";
import { useLiveDashboardStats } from "../../../global/hooks/useLiveDashboardStats";
import GlobalDataTable from "../../../global/GlobalDataTable";
import GlobalBadge from "../../../global/GlobalBadge";
import { GlobalTableActionButton } from "../../../global/GlobalTableActionButton";
import {
  GlobalButton,
  GlobalContactNumberField,
  GlobalEmailField,
  GlobalPasswordField,
  GlobalSelectField,
  GlobalTextAreaField,
  GlobalTextField,
} from "../../../global/forms/GlobalFormFields";
import GlobalFormShell from "../../../global/forms/GlobalFormShell";
import { GlobalDirectoryPaginationSection } from "../../../global/directory";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";
import "../styles/VolunteerTable.css";

const ALERT_ID = "donor-form-alert";
const DIRECTORY_PAGE_SIZE = 50;
const COLUMN_VISIBILITY_KEY = "girinestham.donorList.columnVisibility";
const EMPTY_FORM = {
  donor_name: "",
  address: "",
  mobile_number: "",
  email: "",
  password: "",
};
const COLUMN_DEFS = [
  { id: "donor_code", label: "Donor ID", defaultVisible: true },
  { id: "donor_name", label: "Donor name", defaultVisible: true },
  { id: "mobile_number", label: "Mobile", defaultVisible: true },
  { id: "email", label: "Email", defaultVisible: true },
  { id: "address", label: "Address", defaultVisible: true },
  { id: "total_donation_amount", label: "Total donations", defaultVisible: true },
  { id: "last_donation_date", label: "Last donation", defaultVisible: true },
  { id: "status_name", label: "Status", defaultVisible: true },
];

function safeErrorText(err, fallback = "Something went wrong. Please try again.") {
  const m = err?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

function formatMoney(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n)
    ? `₹${new Intl.NumberFormat("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n)}`
    : "—";
}

function formatDateCell(value) {
  if (value == null || value === "") return "—";
  try {
    const raw = String(value).trim();
    let d = parseISO(raw);
    if (!isValid(d) && /^\d{4}-\d{2}-\d{2}/.test(raw)) d = new Date(raw.replace(" ", "T"));
    if (!isValid(d)) d = new Date(raw);
    return isValid(d) ? format(d, "PP") : "—";
  } catch {
    return "—";
  }
}

function isDonorActive(row) {
  if (row?.is_active != null) return Number(row.is_active) === 1;
  const status = String(row?.status_name || "").toLowerCase().trim();
  return status === "active";
}

function donorRowToForm(row) {
  return {
    donor_name: row?.donor_name ?? "",
    address: row?.address ?? "",
    mobile_number: row?.mobile_number ?? "",
    email: row?.email ?? "",
    password: "",
  };
}

function loadSavedVisibility() {
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return null;
}

function validateForm(formData, isEditMode) {
  const errors = {};
  const name = String(formData.donor_name || "").trim();
  const mobile = String(formData.mobile_number || "").trim();
  const email = String(formData.email || "").trim();
  const address = String(formData.address || "").trim();
  const password = String(formData.password || "").trim();
  const donorNameRegex = /^[A-Za-z]+(?:[ .][A-Za-z]+)*$/;
  const donorPasswordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

  if (!name) errors.donor_name = "Name is required";
  else if (name.length < 4) errors.donor_name = "Name must be at least 4 characters";
  else if (!donorNameRegex.test(name) || /\s{2,}/.test(name) || /\.{2,}/.test(name)) {
    errors.donor_name = "Use letters only, with single spaces or single dots";
  }

  if (!mobile) errors.mobile_number = "Mobile number is required";
  else if (!/^\d{10}$/.test(mobile)) errors.mobile_number = "Mobile must be exactly 10 digits";

  if (!email) errors.email = "Email is required";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = "Invalid email format";
  if (!address) errors.address = "Address is required";
  else if (address.length > 1000) errors.address = "Address must be less than 1000 characters";
  if (!isEditMode && !password) errors.password = "Password is required";
  else if (password && !donorPasswordRegex.test(password)) {
    errors.password =
      "Password must be at least 8 characters with one uppercase letter, one number, and one special character";
  }

  return errors;
}

function escapeCsvCell(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, "\"\"")}"`;
  return s;
}

function exportDonorsCsv(rows) {
  const headers = ["Donor ID", "Donor name", "Mobile", "Email", "Address", "Total donations", "Last donation", "Status"];
  const lines = [headers.join(",")];
  for (const r of rows) {
    lines.push(
      [
        escapeCsvCell(r.donor_code),
        escapeCsvCell(r.donor_name),
        escapeCsvCell(r.mobile_number),
        escapeCsvCell(r.email),
        escapeCsvCell(r.address),
        escapeCsvCell(r.total_donation_amount),
        escapeCsvCell(r.last_donation_date),
        escapeCsvCell(r.status_name),
      ].join(","),
    );
  }
  const blob = new Blob(["\ufeff", lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, "0");
  const d = String(today.getDate()).padStart(2, "0");
  a.download = `donors-${y}-${m}-${d}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function DonorsListPage() {
  const roleSlug = getJwtRoleSlug();
  const isSubAdminDonorPage = roleSlug === "sub-admin";
  const isSubAdminOrVolunteerDonorPage =
    roleSlug === "sub-admin" || roleSlug === "volunteer";
  const [statsReloadToken, setStatsReloadToken] = useState(0);
  const [donors, setDonors] = useState([]);
  const [loadingDonors, setLoadingDonors] = useState(true);
  const [directoryPage, setDirectoryPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterColumn, setFilterColumn] = useState("");
  const [filterValue, setFilterValue] = useState("all");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [activeQuickJump, setActiveQuickJump] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingDonor, setEditingDonor] = useState(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formErrorDetails, setFormErrorDetails] = useState({});
  const [saveAsActive, setSaveAsActive] = useState(true);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewDetails, setViewDetails] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmType, setConfirmType] = useState(null);
  const [donorToHandle, setDonorToHandle] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const formAlertRef = useRef(null);
  const pickerRef = useRef(null);
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = loadSavedVisibility();
    const initial = {};
    for (const c of COLUMN_DEFS) initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
    return initial;
  });

  const isEditMode = Boolean(editingDonor?.id);

  const { dashboardStats, loadingStats, refreshStats } = useLiveDashboardStats({
    fetcher: getDonorDashboard,
    selectData: (data) => data?.data ?? null,
    refreshDeps: [statsReloadToken],
  });

  /** Same as refreshStats — kept so legacy calls after save/status change still compile. */
  const fetchDashboardStats = refreshStats;

  const fetchDonorsList = useCallback(async () => {
    setLoadingDonors(true);
    setErrorMsg("");
    try {
      const q = debouncedSearch.trim();
      const res = q ? await searchDonors(q) : await getAllDonors();
      setDonors(res?.data || []);
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setLoadingDonors(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setDirectoryPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!pickerRef.current?.contains(e.target)) setColumnPickerOpen(false);
    };
    if (columnPickerOpen) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [columnPickerOpen]);

  useEffect(() => {
    fetchDonorsList();
  }, [fetchDonorsList]);

  const setFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrorDetails[name]) setFormErrorDetails((prev) => ({ ...prev, [name]: "" }));
  };

  const visibleDataColumns = useMemo(
    () => COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility],
  );
  const tableHeaders = useMemo(
    () => [
      ...visibleDataColumns.map((c) => ({ key: c.id, label: c.label })),
      { key: "actions", label: "Actions", className: "ub-table__col-actions" },
    ],
    [visibleDataColumns],
  );

  const filterValueOptions = useMemo(() => {
    if (!filterColumn) return [];
    const distinct = [...new Set((donors || []).map((row) => String(row?.[filterColumn] ?? "").trim()).filter(Boolean))];
    distinct.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    return distinct.map((v) => ({ value: v, label: v }));
  }, [donors, filterColumn]);

  const columnFilterOptions = useMemo(
    () => COLUMN_DEFS.map((c) => ({ value: c.id, label: c.label })),
    [],
  );

  const filteredDonors = useMemo(() => {
    if (!filterColumn || filterValue === "all") return donors;
    return donors.filter((row) => String(row?.[filterColumn] ?? "").trim() === String(filterValue).trim());
  }, [donors, filterColumn, filterValue]);

  const directoryTotal = filteredDonors.length;
  const directoryTotalPages = Math.max(1, Math.ceil(directoryTotal / DIRECTORY_PAGE_SIZE));
  const currentPage = Math.min(directoryPage, directoryTotalPages);
  const pagedDonors = useMemo(() => {
    const start = (currentPage - 1) * DIRECTORY_PAGE_SIZE;
    return filteredDonors.slice(start, start + DIRECTORY_PAGE_SIZE);
  }, [filteredDonors, currentPage]);

  const handleFormSuccess = (msg) => {
    setSuccessMsg(msg);
    setFormError("");
    setFormErrorDetails({});
    setEditingDonor(null);
    setFormData(EMPTY_FORM);
    setSaveAsActive(true);
    setStatsReloadToken((t) => t + 1);
    refreshStats();
    fetchDonorsList();
  };

  const handleSubmitDonor = async (e) => {
    e.preventDefault();
    const errors = validateForm(formData, isEditMode);
    setFormErrorDetails(errors);
    setFormError("");
    if (Object.keys(errors).length > 0) {
      setFormError("Please check the highlighted fields below.");
      return;
    }
    setFormSubmitting(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        if (csrfToken) localStorage.setItem("csrf-token", csrfToken);
      }
      const body = {
        donor_name: formData.donor_name.trim(),
        address: formData.address?.trim() || "",
        mobile_number: formData.mobile_number?.trim() || "",
        email: formData.email?.trim() || "",
      };
      const password = formData.password?.trim();
      if (password) {
        body.password = password;
      }
      if (!isEditMode && !isSubAdminDonorPage) {
        body.is_active_donor = saveAsActive;
      }
      if (isEditMode) {
        await updateDonor(editingDonor.id, body, csrfToken);
        const targetActive = saveAsActive ? 1 : 0;
        const currentActive = isDonorActive(editingDonor) ? 1 : 0;
        if (targetActive !== currentActive) {
          await updateDonorStatus(editingDonor.id, targetActive, csrfToken);
        }
        handleFormSuccess("Donor updated successfully.");
      } else {
        await createDonor(body, csrfToken);
        handleFormSuccess("Donor created successfully.");
      }
    } catch (err) {
      setFormError(safeErrorText(err));
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleRequestEdit = (row) => {
    setEditingDonor(row);
    setFormData(donorRowToForm(row));
    setSaveAsActive(isDonorActive(row));
    setFormError("");
    setFormErrorDetails({});
    requestAnimationFrame(() => {
      document.getElementById("donor-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleConfirmStatus = async () => {
    if (!donorToHandle || !confirmType) return;
    setIsConfirming(true);
    setErrorMsg("");
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        if (csrfToken) localStorage.setItem("csrf-token", csrfToken);
      }
      const targetActive = confirmType === "deactivate" ? 0 : 1;
      await updateDonorStatus(donorToHandle.id, targetActive, csrfToken);
      setSuccessMsg(confirmType === "deactivate" ? "Donor deactivated successfully." : "Donor activated successfully.");
      setShowConfirmModal(false);
      setDonorToHandle(null);
      setConfirmType(null);
      setStatsReloadToken((t) => t + 1);
    refreshStats();
      fetchDonorsList();
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setIsConfirming(false);
    }
  };

  const handleView = async (row) => {
    setViewOpen(true);
    setViewLoading(true);
    setViewDetails(null);
    setErrorMsg("");
    try {
      const res = await getDonorDetails(row.id);
      setViewDetails(res?.data);
    } catch (err) {
      setErrorMsg(safeErrorText(err));
    } finally {
      setViewLoading(false);
    }
  };

  const clearDirectoryFilters = () => {
    setFilterColumn("");
    setFilterValue("all");
  };

  const toggleColumn = (id) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      const visibleCount = COLUMN_DEFS.filter((c) => next[c.id]).length;
      if (visibleCount === 0) return prev;
      try {
        localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const QUICK_JUMP_ITEMS = [
    { key: "stats", label: "Overview", targetId: "donor-stats" },
    { key: "form", label: "Add / edit donor", targetId: "donor-form" },
    { key: "list", label: "Directory", targetId: "donor-directory" },
  ];

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">Administrative Area</p>
          <h2 className="user-builder-hero__title">Donor management</h2>
        </div>
      </header>
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalFeedback type="error" text={errorMsg} />

      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          <div id="donor-stats" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <div className="user-builder-panel__head">
              <h3 className="user-builder-panel__title">Overview</h3>
            </div>
            <GlobalMetricCards
              items={[
                { key: "total_donors", label: "Total donors", value: dashboardStats?.total_donors ?? 0, tone: "slate" },
                { key: "total_donations", label: "Total donations", value: formatMoney(dashboardStats?.total_donations), tone: "teal" },
                { key: "active_donors", label: "Active donors", value: dashboardStats?.active_donors ?? 0, tone: "mint" },
                { key: "monthly_donations", label: "Monthly donations", value: formatMoney(dashboardStats?.monthly_donations), tone: "coral" },
                { key: "pending_verification_amount", label: "Pending to verify", value: formatMoney(dashboardStats?.pending_verification_amount), tone: "slate" },
              ]}
              loading={loadingStats}
              ariaLabel="Donor dashboard statistics"
            />
          </div>

          <div id="donor-form" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <div className="user-builder-panel__head">
              <h3 className="user-builder-panel__title">{isEditMode ? "Edit donor" : "Add donor"}</h3>
            </div>
            <form onSubmit={handleSubmitDonor} noValidate>
              <GlobalFormShell
                legend={isEditMode ? "Donor details" : "New donor"}
                showAlert={Boolean(formError)}
                alertText={formError}
                alertId={ALERT_ID}
                overflowVisible
                actions={
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <GlobalButton type="submit" variant="primary" disabled={formSubmitting}>
                      {formSubmitting ? "Saving…" : isEditMode ? "Save changes" : "Create donor"}
                    </GlobalButton>
                    {isEditMode ? (
                      <GlobalButton type="button" variant="ghost" disabled={formSubmitting} onClick={() => {
                        setEditingDonor(null);
                        setFormData(EMPTY_FORM);
                        setSaveAsActive(true);
                        setFormError("");
                        setFormErrorDetails({});
                      }}>
                        Cancel edit
                      </GlobalButton>
                    ) : null}
                  </div>
                }
              >
                <GlobalTextField label="Donor name (person or organization)" value={formData.donor_name} onChange={(v) => setFormField("donor_name", v)} error={formErrorDetails.donor_name} baseAlertId={ALERT_ID} required disabled={formSubmitting} />
                <GlobalContactNumberField label="Mobile" value={formData.mobile_number} onChange={(v) => setFormField("mobile_number", v)} error={formErrorDetails.mobile_number} baseAlertId={ALERT_ID} required disabled={formSubmitting} />
                <GlobalEmailField label="Email" value={formData.email} onChange={(v) => setFormField("email", v)} error={formErrorDetails.email} baseAlertId={ALERT_ID} required disabled={formSubmitting} />
                <GlobalPasswordField
                  label={isEditMode ? "Password (leave blank to keep current)" : "Password"}
                  value={formData.password}
                  onChange={(v) => setFormField("password", v)}
                  placeholder={isEditMode ? "Leave blank to keep current" : ""}
                  error={formErrorDetails.password}
                  baseAlertId={ALERT_ID}
                  required={!isEditMode}
                  disabled={formSubmitting}
                />
                <GlobalTextAreaField
                  label="Address"
                  value={formData.address}
                  onChange={(v) => setFormField("address", v)}
                  error={formErrorDetails.address}
                  baseAlertId={ALERT_ID}
                  rows={3}
                  required
                  disabled={formSubmitting}
                  classNames={{ fieldWrap: "gff-field gff-field--full" }}
                />
                {!isSubAdminDonorPage ? (
                  <div
                    className="ub-status-checkboxes"
                    role="group"
                    aria-label="Donor status"
                  >
                    <label>
                      <input
                        type="checkbox"
                        checked={saveAsActive}
                        onChange={() => setSaveAsActive(true)}
                      />
                      Active
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={!saveAsActive}
                        onChange={() => setSaveAsActive(false)}
                      />
                      Inactive
                    </label>
                  </div>
                ) : null}
              </GlobalFormShell>
            </form>
          </div>

          <div id="donor-directory" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <div className="user-builder-panel__head">
              <h3 className="user-builder-panel__title">Directory</h3>
              <p className="user-builder-panel__hint">Search and manage all donors by name.</p>
            </div>
            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "1rem" }}>
              <div style={{ flex: "1 1 320px" }}>
                <GlobalTextField label="Search donors by name" value={searchInput} onChange={(v) => setSearchInput(v)} placeholder="Enter donor name" />
              </div>
              <div style={{ flex: "0 0 220px" }}>
                <GlobalSelectField
                  label="Column"
                  value={filterColumn}
                  onChange={(v) => {
                    setFilterColumn(v);
                    setFilterValue("all");
                  }}
                  options={columnFilterOptions}
                  placeholderOption="Select"
                />
              </div>
              <div style={{ flex: "0 0 180px" }}>
                <GlobalSelectField
                  label="Value"
                  value={filterValue}
                  onChange={setFilterValue}
                  options={filterValueOptions}
                  placeholderOption="All"
                  disabled={!filterColumn}
                />
              </div>
              <GlobalButton type="button" variant="ghost" onClick={clearDirectoryFilters}>
                Clear filter
              </GlobalButton>
            </div>
            <div
              className="ub-directory-table-toolbar"
              style={{ display: "flex", alignItems: "center", gap: "0.65rem", marginBottom: "0.65rem", width: "100%" }}
            >
              <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", padding: "0.2rem 0" }}>
                <div className="vt-column-picker" ref={pickerRef}>
                  <GlobalButton
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setColumnPickerOpen((o) => !o)}
                    aria-expanded={columnPickerOpen}
                    aria-controls="donor-list-column-panel"
                    style={{ width: "fit-content", whiteSpace: "nowrap" }}
                  >
                    <Columns3 size={16} aria-hidden />
                    <span style={{ marginLeft: "0.35rem" }}>Columns</span>
                  </GlobalButton>
                  {columnPickerOpen ? (
                    <div id="donor-list-column-panel" className="vt-column-picker__panel" role="group" aria-label="Choose visible columns">
                      <p className="vt-column-picker__title">Show columns</p>
                      <ul className="vt-column-picker__list">
                        {COLUMN_DEFS.map((c) => (
                          <li key={c.id}>
                            <label className="vt-column-picker__row">
                              <input type="checkbox" checked={!!columnVisibility[c.id]} onChange={() => toggleColumn(c.id)} />
                              <span>{c.label}</span>
                            </label>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </div>
              <GlobalButton
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => exportDonorsCsv(filteredDonors)}
                disabled={loadingDonors || filteredDonors.length === 0}
                style={{ flexShrink: 0, width: "fit-content", whiteSpace: "nowrap", minWidth: "110px" }}
              >
                Export CSV
              </GlobalButton>
            </div>

            {loadingDonors ? (
              <p className="ub-loading">Loading donors…</p>
            ) : (
              <div className="ub-table-section">
                <GlobalDataTable wrapClassName="ub-table-wrap" tableClassName="ub-table" tableAriaLabel="Donors list" headers={tableHeaders}>
                  {pagedDonors.length === 0 ? (
                    <tr><td colSpan={Math.max(visibleDataColumns.length + 1, 1)} style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}>No donors found.</td></tr>
                  ) : (
                    pagedDonors.map((row) => (
                      <tr key={row.id}>
                        {visibleDataColumns.map((col) => (
                          <td key={col.id}>
                            {col.id === "status_name" ? (
                              <GlobalBadge active={isDonorActive(row)} activeText={row.status_name || "Active"} inactiveText={row.status_name || "Inactive"} />
                            ) : col.id === "total_donation_amount" ? (
                              formatMoney(row.total_donation_amount)
                            ) : col.id === "last_donation_date" ? (
                              formatDateCell(row.last_donation_date)
                            ) : col.id === "address" ? (
                              row.address ? (String(row.address).length > 48 ? `${String(row.address).slice(0, 45)}…` : row.address) : "—"
                            ) : (
                              row[col.id] ?? "—"
                            )}
                          </td>
                        ))}
                        <td className="ub-table__col-actions">
                          <div className="ub-table-actions">
                            <GlobalTableActionButton action="view" variant="ghost" onClick={() => handleView(row)} />
                            {!isSubAdminOrVolunteerDonorPage ? (
                              <GlobalTableActionButton action="edit" variant="ghost" onClick={() => handleRequestEdit(row)} />
                            ) : null}
                            {isDonorActive(row) ? (
                              !isSubAdminOrVolunteerDonorPage ? (
                                <GlobalTableActionButton
                                  action="deactivate"
                                  variant="ghost"
                                  onClick={() => { setDonorToHandle(row); setConfirmType("deactivate"); setShowConfirmModal(true); }}
                                />
                              ) : null
                            ) : !isSubAdminDonorPage ? (
                              <GlobalTableActionButton
                                action="activate"
                                variant="ghost"
                                onClick={() => { setDonorToHandle(row); setConfirmType("activate"); setShowConfirmModal(true); }}
                              />
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </GlobalDataTable>
                <GlobalDirectoryPaginationSection
                  page={currentPage}
                  totalPages={directoryTotalPages}
                  total={directoryTotal}
                  pageSize={DIRECTORY_PAGE_SIZE}
                  onPageChange={setDirectoryPage}
                  label="Donor directory pagination"
                />
              </div>
            )}
          </div>
        </div>
        <GlobalQuickJump asideAriaLabel="Donor page sections" items={QUICK_JUMP_ITEMS} activeKey={activeQuickJump} onActiveChange={setActiveQuickJump} />
      </div>

      <GlobalModal open={viewOpen} onClose={() => !viewLoading && setViewOpen(false)} title="Donor profile and history" size="lg" footer={<GlobalButton variant="primary" onClick={() => setViewOpen(false)}>Close</GlobalButton>}>
        {viewLoading ? (
          <p className="ub-loading">Loading donor…</p>
        ) : viewDetails?.donor ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <p><strong>Donor ID:</strong> {viewDetails.donor.donor_code ?? "—"}</p>
            <p><strong>Name:</strong> {viewDetails.donor.donor_name ?? "—"}</p>
            <p><strong>Email:</strong> {viewDetails.donor.email ?? "—"}</p>
            <p><strong>Phone:</strong> {viewDetails.donor.phone ?? viewDetails.donor.contact_number ?? "—"}</p>
            <p><strong>Address:</strong> {viewDetails.donor.address ?? "—"}</p>
            <h4 style={{ margin: "0.5rem 0 0" }}>Donation history</h4>
            {Array.isArray(viewDetails.donations) && viewDetails.donations.length ? (
              <GlobalDataTable
                tableAriaLabel="Donor donation history"
                headers={[
                  { key: "receipt", label: "Receipt" },
                  { key: "amount", label: "Amount" },
                  { key: "date", label: "Date" },
                  { key: "purpose", label: "Purpose" },
                ]}
                rows={viewDetails.donations}
                renderRow={(d) => (
                  <tr key={d.id || d.donation_receipt_no}>
                    <td>{d.donation_receipt_no || d.payment_receipt_no || "—"}</td>
                    <td>{d.donation_amount ?? "—"}</td>
                    <td>{d.donation_date ? String(d.donation_date).slice(0, 10) : "—"}</td>
                    <td>{d.purpose_name || d.purpose || "—"}</td>
                  </tr>
                )}
              />
            ) : (
              <p>No donations recorded yet.</p>
            )}
          </div>
        ) : (
          <p>{errorMsg || "No data."}</p>
        )}
      </GlobalModal>

      <GlobalModal
        open={showConfirmModal}
        onClose={() => !isConfirming && setShowConfirmModal(false)}
        title={confirmType === "deactivate" ? "Deactivate donor" : "Activate donor"}
        footer={
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <GlobalButton onClick={() => setShowConfirmModal(false)} disabled={isConfirming}>Cancel</GlobalButton>
            <GlobalButton variant={confirmType === "deactivate" ? "danger" : "primary"} onClick={handleConfirmStatus} disabled={isConfirming}>
              {isConfirming ? "Updating…" : confirmType === "deactivate" ? "Deactivate" : "Activate"}
            </GlobalButton>
          </div>
        }
      >
        <p>Continue for <b>{donorToHandle?.donor_name}</b>?</p>
      </GlobalModal>
    </div>
  );
}
